<template>
  <div class="card card-absolute rfo-card rfo-card--primary oa-section-card">
    <div class="card-header bg-primary text-white">
      <h6 class="mb-0"><i class="fa fa-bolt me-2"></i>Actions</h6>
    </div>
    <div class="card-body">
      <div v-if="!allowedActions || allowedActions.length === 0" class="text-muted small">
        No actions available for your role on this case.
      </div>

      <div class="row g-2" :class="`row-cols-1 row-cols-sm-2 row-cols-lg-${gridCols}`">
        <div v-for="entry in allowedActions" :key="entry.action" class="col">
          <button
            class="btn w-100 d-flex align-items-center justify-content-center"
            :class="actionButtonClass(entry.action)"
            :disabled="!entry.available"
            :title="(entry.blockingReasons || []).join(' · ')"
            @click="$emit('action', entry.action)"
          >
            <i class="fa me-2" :class="actionIcon(entry.action)" aria-hidden="true"></i>
            <span class="text-truncate">{{ actionLabel(entry.action) }}</span>
          </button>
        </div>
      </div>

      <div v-if="warnings.length > 0" class="mt-3">
        <div
          v-for="(w, idx) in warnings"
          :key="idx"
          class="alert alert-warning outline small py-1 px-2 mb-1"
        >
          <i class="fa fa-exclamation-triangle me-1"></i>{{ w }}
        </div>
      </div>

      <div v-if="showCaptureToggle" class="mt-3 border-top pt-2">
        <div class="form-check form-switch">
          <input
            id="captureOnAmend"
            v-model="captureOnAmend"
            class="form-check-input border border-primary"
            type="checkbox"
            @change="$emit('update:captureOnAmend', captureOnAmend)"
          />
          <label class="form-check-label small" for="captureOnAmend">
            Capture on Amend
            <i
              class="fa fa-question-circle text-muted ms-1"
              title="When enabled, the payment will be captured immediately upon amendment. Value is audited."
            ></i>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  allowedActions: { type: Array, default: () => [] },
  warnings: { type: Array, default: () => [] },
  showCaptureToggle: { type: Boolean, default: false },
  initialCaptureOnAmend: { type: Boolean, default: false },
});

defineEmits(['action', 'update:captureOnAmend']);

const captureOnAmend = ref(props.initialCaptureOnAmend);
watch(
  () => props.initialCaptureOnAmend,
  (val) => {
    captureOnAmend.value = val;
  },
);

// Match grid columns to the actual number of actions so each button gets an even
// share of the row (typical pay-ops view is 3 actions for agents, 4 for admins).
const gridCols = computed(() => Math.min(Math.max(props.allowedActions.length, 1), 4));

function actionLabel(action) {
  switch (action) {
    case 'completeManually':
      return 'Complete Manually';
    case 'cancelManually':
      return 'Cancel Manually';
    case 'forceCompleteManually':
      return 'Force Complete';
    case 'refundAndCancelManually':
      return 'Refund & Cancel';
    default:
      return action;
  }
}

function actionIcon(action) {
  switch (action) {
    case 'completeManually':
      return 'fa-check';
    case 'cancelManually':
      return 'fa-times';
    case 'forceCompleteManually':
      return 'fa-gavel';
    case 'refundAndCancelManually':
      return 'fa-undo';
    default:
      return 'fa-cog';
  }
}

function actionButtonClass(action) {
  switch (action) {
    case 'completeManually':
      return 'btn-success';
    case 'cancelManually':
      return 'btn-danger';
    case 'forceCompleteManually':
      return 'btn-warning';
    case 'refundAndCancelManually':
      return 'btn-dark';
    default:
      return 'btn-secondary';
  }
}
</script>
